import React, { useState, useEffect, useMemo } from 'react';
import { useParams, Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  FaSearch,
  FaStar,
  FaMapMarkerAlt,
  FaEye,
  FaPaperPlane,
  FaShieldAlt,
  FaChevronLeft,
  FaChevronRight,
  FaList,
  FaTh,
  FaRedo,
  FaTimes,
  FaQuestionCircle,
  FaIndustry,
  FaCheckCircle,
} from 'react-icons/fa';
import { toast } from 'react-toastify';
import {
  CM_INDUSTRY_BROWSE_OPTIONS,
  CM_INDUSTRY_IMAGE_BY_SLUG,
  industrySlugToLabel,
} from '../../constants/industryTypeMap';
import { CONTRACT_MANUFACTURING_TYPE_MAP } from '../../constants/contractManufacturingTypeMap';
import { FACILITY_TYPE_MAP } from '../../constants/facilityTypeMap';
import CommonService from '../../services/common.service';
import b2bHubHomeService from '../../services/b2bHubHome.service';
import cmIndustryTypeService from '../../services/cmIndustryType.service';
import StorageService from '../../services/StorageService';
import { API_URLS, resolvePublicFileUrl } from '../../constants/api-urls';
import B2BHubHomeHeader from '../../components/B2BHubHomeHeader';
import HubFloatingRail from '../../components/hub/HubFloatingRail';
import {
  CategoryBrowseGrid,
  CategoryTabsRow,
  FilterSelect,
} from '../../components/marketplace/MarketplaceBrowseUi';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../../assets/styles/home.scss';
import '../../assets/styles/product_marketplace.scss';
import '../../assets/styles/auction_marketplace.scss';
import { readSearchQueryFromParams } from '../../config/globalHeaderSearch';

/** Same value keys as Contract Manufacturer Profile → Production → Quality certifications (for filter matching). */
const CM_CERT_FILTER_OPTIONS = [
  { value: 'iso_9001_2015', label: 'ISO 9001:2015 – Quality Management Systems' },
  { value: 'iso_13485_2016', label: 'ISO 13485:2016 – Medical Devices' },
  { value: 'iatf_16949_2016', label: 'IATF 16949:2016 – Automotive Quality Management' },
  { value: 'as9100d', label: 'AS9100D – Aerospace Quality Management' },
  { value: 'iso_14001_2015', label: 'ISO 14001:2015 – Environmental Management Systems' },
  { value: 'iso_45001_2018', label: 'ISO 45001:2018 – Occupational Health & Safety' },
  { value: 'iso_22000_2018', label: 'ISO 22000:2018 – Food Safety Management' },
  { value: 'iso_27001_2013', label: 'ISO 27001:2013 – Information Security Management' },
  { value: 'iso_50001_2018', label: 'ISO 50001:2018 – Energy Management Systems' },
  { value: 'iso_22301_2019', label: 'ISO 22301:2019 – Business Continuity Management' },
  { value: 'iso_20000_1_2018', label: 'ISO 20000-1:2018 – IT Service Management' },
  { value: 'sa_8000_2014', label: 'SA 8000:2014 – Social Accountability' },
  { value: 'ce_marking', label: 'CE Marking – Conformity for Products in the European Market' },
  { value: 'fda_approval', label: 'FDA Approval – U.S. Food and Drug Administration Approval' },
  { value: 'fcc_certification', label: 'FCC Certification – Federal Communications Commission Approval' },
  { value: 'cb_scheme', label: 'CB Scheme – International Electrotechnical Certification' },
];

const CM_PAGE_SIZE = 12;
const CM_PAGE_SIZE_OPTIONS = [12, 24, 36, 48];
const CM_SORT_OPTIONS = [
  { value: 'relevance', label: 'Relevance' },
  { value: 'name', label: 'Name A-Z' },
  { value: 'rating', label: 'Rating (High to Low)' },
];

function normalizeIndustrySlug(slug) {
  if (!slug || typeof slug !== 'string') return undefined;
  const s = slug.trim().replace(/-+$/, '');
  if (!s || s === 'all') return undefined;
  return s;
}

function ContractManufacturersIndustryBrowse() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [industries, setIndustries] = useState(
    CM_INDUSTRY_BROWSE_OPTIONS.map((t) => ({ ...t, count: 0 })),
  );

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    b2bHubHomeService
      .getHome()
      .then((res) => {
        if (cancelled) return;
        const fromApi = Array.isArray(res?.data?.contract_manufacturer_industries)
          ? res.data.contract_manufacturer_industries
          : [];
        if (fromApi.length) {
          setIndustries(fromApi);
          return;
        }
        setIndustries(CM_INDUSTRY_BROWSE_OPTIONS.map((t) => ({ ...t, count: 0 })));
      })
      .catch(() => {
        if (!cancelled) setIndustries(CM_INDUSTRY_BROWSE_OPTIONS.map((t) => ({ ...t, count: 0 })));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const industryItems = useMemo(
    () =>
      [...industries]
        .sort((a, b) => (b.count - a.count) || a.name.localeCompare(b.name))
        .map((t) => ({
          id: t.slug,
          name: t.count > 0 ? `${t.name} (${t.count})` : t.name,
          imagePath: t.image_path || CM_INDUSTRY_IMAGE_BY_SLUG[t.slug] || null,
        })),
    [industries],
  );

  return (
    <div className="product-marketplace-page">
      <B2BHubHomeHeader />
      <main className="product-marketplace-main">
        <nav className="product-marketplace-breadcrumb" aria-label="Breadcrumb">
          <Link to="/home">Home</Link>
          <span className="product-marketplace-breadcrumb__sep">&gt;</span>
          <span className="product-marketplace-breadcrumb__current">Contract Manufacturers</span>
        </nav>

        <div className="product-marketplace-heading">
          <div className="product-marketplace-heading__text">
            <h1>Contract Manufacturers</h1>
            <p>Browse industry types step by step to find verified partners.</p>
          </div>
        </div>

        <CategoryBrowseGrid
          title="Browse by Industry Type"
          subtitle="Select an industry to explore contract manufacturer listings."
          items={industryItems}
          loading={loading}
          emptyMessage="No industry types available yet."
          onSelect={(slug) => navigate(`/contract-manufacturers/${slug}`)}
        />
      </main>
      <HubFloatingRail />
    </div>
  );
}

function ContractManufacturersListView({ industrySlug }) {
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(true);
  const [list, setList] = useState([]);
  const [total, setTotal] = useState(0);
  const [search, setSearch] = useState(() => readSearchQueryFromParams(searchParams));
  const [sortBy, setSortBy] = useState('relevance');
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [featuredOnly, setFeaturedOnly] = useState(false);
  const [ratingFilter, setRatingFilter] = useState(null);
  const [selectedIndustryFilters, setSelectedIndustryFilters] = useState([]);
  const [selectedStateFilter, setSelectedStateFilter] = useState('');
  const [selectedCityFilter, setSelectedCityFilter] = useState('');
  const [selectedCmTypeFilter, setSelectedCmTypeFilter] = useState('');
  const [selectedFacilityTypeFilter, setSelectedFacilityTypeFilter] = useState('');
  const [selectedCertFilters, setSelectedCertFilters] = useState([]);
  const [industryFilterOptions, setIndustryFilterOptions] = useState([]);
  const [error, setError] = useState(null);
  const [showingAllDueToEmpty, setShowingAllDueToEmpty] = useState(false);
  const [retryCount, setRetryCount] = useState(0);
  const [rfqSentCompanyIds, setRfqSentCompanyIds] = useState(new Set());
  const [viewMode, setViewMode] = useState('grid');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(CM_PAGE_SIZE);
  const [industryTabs, setIndustryTabs] = useState([]);

  const navigate = useNavigate();
  const activeIndustryLabel = useMemo(() => {
    const fromTabs = industryTabs.find((t) => t.slug === industrySlug);
    if (fromTabs?.name) return fromTabs.name.replace(/\s*\(\d+\)\s*$/, '').trim();
    return industrySlugToLabel(industrySlug) || industrySlug;
  }, [industryTabs, industrySlug]);

  const doRetry = () => {
    setError(null);
    setRetryCount((c) => c + 1);
  };

  useEffect(() => {
    setSearch(readSearchQueryFromParams(searchParams));
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    setError(null);
    setShowingAllDueToEmpty(false);
    const fetchList = async (typeFilter) => {
      setLoading(true);
      try {
        const params = { industry: typeFilter || undefined, page: 1, limit: 200 };
        const requests = [CommonService.get(API_URLS.CONTRACT_MANUFACTURER.LIST, params)];
        const canLoadMySubmissions = StorageService.isAuthenticated();
        if (canLoadMySubmissions) {
          requests.push(
            CommonService.get(API_URLS.CONTRACT_MANUFACTURER.MY_RFQ_SUBMISSIONS, { limit: 500, offset: 0 })
          );
        }
        const [listRes, submissionsRes] = await Promise.allSettled(requests);
        if (listRes.status !== 'fulfilled') throw listRes.reason;
        const data = listRes.value?.data ?? listRes.value;
        const items = Array.isArray(data?.items) ? data.items : [];
        const totalCount = data?.total ?? items.length;
        if (cancelled) return;
        setList(items);
        setTotal(totalCount);
        if (submissionsRes?.status === 'fulfilled') {
          const submissionsData = submissionsRes.value?.data ?? submissionsRes.value ?? {};
          const submissionItems = Array.isArray(submissionsData?.items) ? submissionsData.items : [];
          const sentIds = new Set(
            submissionItems
              .map((row) => Number(row?.company_id))
              .filter((n) => Number.isFinite(n) && n > 0)
          );
          setRfqSentCompanyIds(sentIds);
        } else {
          setRfqSentCompanyIds(new Set());
        }
        setError(null);
      } catch (err) {
        if (!cancelled) {
          setList([]);
          setTotal(0);
          setRfqSentCompanyIds(new Set());
          const status = err?.response?.status;
          const msg = err?.response?.data?.message || err?.message;
          const statusHint = status ? ` (${status})` : '';
          setError(msg ? `${msg}${statusHint}` : `Unable to load list${statusHint}. Check that the backend is running and the API URL is correct.`);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchList(industrySlug);
    return () => { cancelled = true; };
  }, [industrySlug, retryCount]);

  useEffect(() => {
    let cancelled = false;
    b2bHubHomeService
      .getHome()
      .then((res) => {
        if (cancelled) return;
        const fromApi = Array.isArray(res?.data?.contract_manufacturer_industries)
          ? res.data.contract_manufacturer_industries
          : [];
        setIndustryTabs(
          fromApi.length
            ? fromApi
            : CM_INDUSTRY_BROWSE_OPTIONS.map((t) => ({ ...t, count: 0 })),
        );
      })
      .catch(() => {
        if (!cancelled) {
          setIndustryTabs(CM_INDUSTRY_BROWSE_OPTIONS.map((t) => ({ ...t, count: 0 })));
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    let cancelled = false;
    cmIndustryTypeService
      .listActive()
      .then((res) => {
        if (cancelled) return;
        const items = Array.isArray(res?.data?.items) ? res.data.items : [];
        if (items.length) {
          setIndustryFilterOptions(items.map((row) => ({ value: row.name, label: row.name })));
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  const industryTabItems = useMemo(
    () =>
      industryTabs.map((t) => ({
        id: t.slug,
        name: t.count > 0 ? `${t.name} (${t.count})` : t.name,
        imagePath: t.image_path || CM_INDUSTRY_IMAGE_BY_SLUG[t.slug] || null,
      })),
    [industryTabs],
  );

  const normalizeImageRef = (ref) => {
    if (ref == null || ref === '') return null;
    if (typeof ref === 'string') {
      const t = ref.trim();
      return t || null;
    }
    if (typeof ref === 'object') {
      const u =
        ref.url
        || ref.accessPath
        || ref.filePath
        || ref.src
        || ref.path
        || ref.imageUrl
        || ref.image_url;
      return u && String(u).trim() ? String(u).trim() : null;
    }
    return null;
  };

  const getCardImage = (item) => {
    const fd = item.facility_details || {};
    const images = fd.facilityImages || fd.facility_images || fd.facilityImageUrls || fd.facility_image_urls;
    if (typeof images === 'string' && images.trim()) {
      const u = normalizeImageRef(images.trim());
      if (u) return u;
    }
    if (Array.isArray(images)) {
      for (let i = 0; i < images.length; i += 1) {
        const u = normalizeImageRef(images[i]);
        if (u) return u;
      }
    }
    const banner = normalizeImageRef(
      fd.bannerImageUrl || fd.banner_image_url || fd.listingBannerUrl
    );
    if (banner) return banner;
    const prod = item.product_details || {};
    if (Array.isArray(prod.products)) {
      for (let j = 0; j < prod.products.length; j += 1) {
        const p = prod.products[j];
        const u = normalizeImageRef(
          p.productCatalogueUrl
            || p.product_catalogue_url
            || p.productCompanyLogoUrl
            || p.product_company_logo_url
        );
        if (u) return u;
      }
    }
    const rootCat = normalizeImageRef(
      prod.productCatalogueUrl || prod.product_catalogue_url || prod.product_catalogue
    );
    if (rootCat) return rootCat;
    return null;
  };

  const isFeaturedManufacturer = (item) => {
    const fd = item.facility_details || {};
    return Boolean(
      item.featured
        || fd.featured
        || fd.isFeatured
        || fd.listing_featured
        || fd.listingFeatured
    );
  };

  const getRatingDisplay = (item) => {
    const fd = item.facility_details || {};
    const raw =
      fd.ratingScore ?? fd.rating_score ?? fd.avgRating ?? item.rating_score ?? item.rating;
    const n = raw != null && raw !== '' ? Number(raw) : NaN;
    if (!Number.isFinite(n) || n <= 0) return null;
    return n.toFixed(1);
  };

  const getReviewCount = (item) => {
    const fd = item.facility_details || {};
    const n = fd.reviewCount ?? fd.review_count ?? item.review_count;
    const num = n != null && n !== '' ? parseInt(String(n), 10) : NaN;
    if (!Number.isFinite(num) || num < 0) return null;
    return num;
  };

  const looksLikeLocationId = (v) => {
    if (v == null || v === '') return false;
    const s = String(v).trim();
    return /^\d+$/.test(s);
  };

  const getLocation = (item) => {
    const fd = item.facility_details || {};
    const cityLabel =
      fd.cityName
      || fd.city_name
      || fd.cityLabel
      || fd.displayCity;
    const stateLabel =
      fd.stateName
      || fd.state_name
      || fd.stateLabel
      || fd.displayState;
    if (cityLabel || stateLabel) {
      return [cityLabel, stateLabel].filter(Boolean).join(', ');
    }

    const cityRaw = fd.city ?? fd.address?.city;
    const stateRaw = fd.state ?? fd.address?.state;
    const addr = item.address || fd.address;

    if (!looksLikeLocationId(cityRaw) && !looksLikeLocationId(stateRaw)) {
      if (cityRaw || stateRaw) return [cityRaw, stateRaw].filter(Boolean).join(', ');
    }

    if (typeof addr === 'string' && addr.trim()) return addr.trim();
    if (addr && typeof addr === 'object') {
      const ac = addr.city;
      const as = addr.state;
      const acountry = addr.country;
      if (!looksLikeLocationId(ac) && !looksLikeLocationId(as)) {
        const parts = [ac, as, acountry].filter(Boolean);
        if (parts.length) return parts.join(', ');
      }
      const partsNamed = [
        addr.city_name || addr.cityName,
        addr.state_name || addr.stateName,
        addr.country_name || addr.countryName,
      ].filter(Boolean);
      if (partsNamed.length) return partsNamed.join(', ');
    }

    if (looksLikeLocationId(cityRaw) || looksLikeLocationId(stateRaw)) {
      return '';
    }
    return '';
  };

  /** State label for filter dropdown (matches getLocation priority for state). */
  const getItemStateForFilter = (item) => {
    const fd = item.facility_details || {};
    const stateLabel =
      fd.stateName
      || fd.state_name
      || fd.stateLabel
      || fd.displayState;
    if (stateLabel && String(stateLabel).trim()) return String(stateLabel).trim();
    const addr = item.address || fd.address;
    if (addr && typeof addr === 'object') {
      const st = addr.state_name || addr.stateName || addr.state;
      if (st && !looksLikeLocationId(st)) return String(st).trim();
    }
    const stateRaw = fd.state ?? fd.address?.state;
    if (stateRaw && !looksLikeLocationId(stateRaw)) return String(stateRaw).trim();
    return '';
  };

  const getItemCityForFilter = (item) => {
    const fd = item.facility_details || {};
    const cityLabel = fd.cityName || fd.city_name || fd.cityLabel || fd.displayCity;
    if (cityLabel && String(cityLabel).trim()) return String(cityLabel).trim();
    const addr = item.address || fd.address;
    if (addr && typeof addr === 'object') {
      const c = addr.city_name || addr.cityName || addr.city;
      if (c && !looksLikeLocationId(c)) return String(c).trim();
    }
    const cityRaw = fd.city ?? fd.address?.city;
    if (cityRaw && !looksLikeLocationId(cityRaw)) return String(cityRaw).trim();
    return '';
  };

  const getItemContractManufacturingType = (item) => {
    const fd = item.facility_details || {};
    const v = fd.contractManufacturingType || fd.contract_manufacturing_type;
    return v != null && String(v).trim() ? String(v).trim() : '';
  };

  const getItemFacilityType = (item) => {
    const fd = item.facility_details || {};
    const v = fd.facilityType || fd.facility_type;
    return v != null && String(v).trim() ? String(v).trim() : '';
  };

  const resolveIndustryOption = (val) => {
    if (!val) return null;
    const vs = String(val).trim();
    const fromApi = industryFilterOptions.find((o) => o.value === vs || o.label === vs);
    if (fromApi) return fromApi;
    return { value: vs, label: vs };
  };

  const industryLabel = (val) => {
    if (!val) return '';
    if (typeof val === 'object' && val.label) return val.label;
    const resolved = resolveIndustryOption(val);
    return resolved ? resolved.label : String(val);
  };

  const getIndustryTags = (item) => {
    const fd = item.facility_details || {};
    const raw = fd.industryTypes || fd.industry_types || [];
    if (!Array.isArray(raw) || raw.length === 0) return [];
    return raw
      .slice(0, 3)
      .map((x) => industryLabel(typeof x === 'string' ? x : x?.value || x?.label || x))
      .filter(Boolean);
  };

  const getItemIndustryValues = (item) => {
    const fd = item.facility_details || {};
    const raw = fd.industryTypes || fd.industry_types || [];
    if (!Array.isArray(raw)) return [];
    const out = new Set();
    raw.forEach((x) => {
      const v = typeof x === 'string' ? x : (x?.value ?? x?.label);
      if (v == null || v === '') return;
      const resolved = resolveIndustryOption(v);
      out.add(resolved ? resolved.value : String(v).trim());
    });
    return [...out];
  };

  const getItemQualityCerts = (item) => {
    const pd = item.production_details || {};
    const raw = pd.qualityCertificationAvailable || pd.quality_certification_available || [];
    if (!Array.isArray(raw)) return [];
    return [...new Set(raw.map((x) => (typeof x === 'string' ? x : x?.value)).filter(Boolean))];
  };

  const periodSuffix = (period) => {
    const p = (period || '').toLowerCase().replace(/_/g, ' ');
    if (!p) return '';
    if (p.includes('month')) return '/month';
    if (p.includes('week')) return '/week';
    if (p.includes('day')) return '/day';
    if (p.includes('year')) return '/year';
    if (p.includes('quarter')) return '/quarter';
    return `/${p.replace(/\s+/g, ' ').trim()}`;
  };

  const formatCapacityFromParts = (v, u, per) => {
    if (v == null || v === '') return null;
    const str = String(v).trim();
    if (!str) return null;
    const num = Number(str.replace(/,/g, ''));
    const formatted = Number.isFinite(num) ? num.toLocaleString() : str;
    const unitPart = (u && String(u).trim()) ? ` ${String(u).trim()}` : '';
    const suffix = periodSuffix(per);
    return `${formatted}${unitPart}${suffix}`.trim();
  };

  const capacityFromProductRow = (p) => {
    if (!p || typeof p !== 'object') return null;
    let v = p.productionCapacityValue ?? p.production_capacity_value;
    const u = (p.productionCapacityUnit || p.production_capacity_unit || '').trim();
    const per = p.productionCapacityPeriod || p.production_capacity_period || '';
    if ((v == null || v === '') && typeof p.productionCapacity === 'string' && p.productionCapacity.trim()) {
      const raw = p.productionCapacity.trim();
      const extracted = raw.replace(/[^0-9.,]/g, '').replace(/,/g, '');
      if (extracted) v = extracted;
      if (!u && /unit/i.test(raw)) {
        return formatCapacityFromParts(v || raw, 'units', per) || raw;
      }
      return formatCapacityFromParts(v, u, per) || raw;
    }
    return formatCapacityFromParts(v, u, per);
  };

  const getCapacity = (item) => {
    const prod = item.product_details || {};
    if (Array.isArray(prod.products)) {
      for (let i = 0; i < prod.products.length; i += 1) {
        const c = capacityFromProductRow(prod.products[i]);
        if (c) return c;
      }
    }
    const rootCap = capacityFromProductRow(prod);
    if (rootCap) return rootCap;

    const fd = item.facility_details || {};
    const mv = fd.monthlyProductionCapacity || fd.monthly_production_capacity;
    if (mv != null && mv !== '') {
      const n = Number(String(mv).replace(/,/g, ''));
      if (Number.isFinite(n)) return `${n.toLocaleString()} units/month`;
    }

    const pd = item.production_details || {};
    const capList = pd.capabilitiesList || pd.capabilities_list;
    if (Array.isArray(capList) && capList[0]) {
      const c0 = capList[0];
      const mv2 = c0.machineCapacityValue ?? c0.machine_capacity_value;
      const mu = c0.machineCapacity || c0.machine_capacity;
      const mp = c0.machineCapacityPeriod || c0.machine_capacity_period;
      const alt = formatCapacityFromParts(mv2, mu, mp);
      if (alt) return alt;
    }
    return null;
  };

  const leadDaysFromRow = (row) => {
    if (!row || typeof row !== 'object') return null;
    const d = row.lead_time_days ?? row.leadTimeDays ?? row.days;
    if (d == null || d === '') return null;
    const days = Number(d);
    return Number.isFinite(days) ? days : null;
  };

  const firstLeadDaysFromList = (lt) => {
    if (!Array.isArray(lt)) return null;
    for (let i = 0; i < lt.length; i += 1) {
      const days = leadDaysFromRow(lt[i]);
      if (days != null) return days;
    }
    return null;
  };

  const formatResponseFromDays = (days) => {
    if (days == null || !Number.isFinite(days)) return null;
    if (days <= 1) return '< 24 hours';
    return `Within ${days} days`;
  };

  const getResponseTime = (item) => {
    const fd = item.facility_details || {};
    const pd = item.production_details || {};
    const hours =
      fd.averageResponseTimeHours
      ?? fd.average_response_time_hours
      ?? pd.averageResponseTimeHours
      ?? pd.average_response_time_hours;
    const h = hours != null && hours !== '' ? Number(hours) : NaN;
    if (Number.isFinite(h) && h > 0) return `< ${Math.ceil(h)} hours`;

    const prod = item.product_details || {};
    const rootLists = [
      prod.lead_time_list,
      prod.leadTimeList,
      prod.lead_time?.items,
      Array.isArray(prod.lead_time) ? prod.lead_time : null,
    ];
    for (let i = 0; i < rootLists.length; i += 1) {
      const days = firstLeadDaysFromList(rootLists[i]);
      const formatted = formatResponseFromDays(days);
      if (formatted) return formatted;
    }

    if (Array.isArray(prod.products)) {
      for (let j = 0; j < prod.products.length; j += 1) {
        const p = prod.products[j];
        const plists = [p.leadTimeList, p.lead_time_list, p.lead_time?.items];
        for (let k = 0; k < plists.length; k += 1) {
          const days = firstLeadDaysFromList(plists[k]);
          const formatted = formatResponseFromDays(days);
          if (formatted) return formatted;
        }
        const single =
          p.leadTimeDays ?? p.lead_time_days ?? p.leadTimeDay ?? p.lead_time_day;
        if (single != null && single !== '') {
          const formatted = formatResponseFromDays(Number(single));
          if (formatted) return formatted;
        }
      }
    }

    const daysSingle = prod.leadTimeDays ?? prod.lead_time_days;
    if (daysSingle != null && daysSingle !== '') {
      return formatResponseFromDays(Number(daysSingle));
    }
    return null;
  };

  const getCompletedOrders = (item) => {
    const fd = item.facility_details || {};
    const pd = item.production_details || {};
    const prod = item.product_details || {};
    const n =
      fd.completedOrders
      ?? fd.completed_orders
      ?? pd.completedOrders
      ?? pd.completed_orders
      ?? prod.completedOrders
      ?? prod.completed_orders
      ?? item.completed_orders;
    if (n == null || n === '') return null;
    const num = parseInt(String(n), 10);
    return Number.isFinite(num) ? num.toLocaleString() : String(n);
  };

  const formatMetric = (value) => (value == null || value === '' ? '—' : value);

  const uniqueStatesFromList = useMemo(() => {
    const set = new Set();
    list.forEach((item) => {
      const st = getItemStateForFilter(item);
      if (st) set.add(st);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [list]);

  const uniqueCitiesFromList = useMemo(() => {
    const set = new Set();
    list.forEach((item) => {
      const city = getItemCityForFilter(item);
      if (city) set.add(city);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [list]);

  const uniqueCmTypesFromList = useMemo(() => {
    const set = new Set();
    list.forEach((item) => {
      const t = getItemContractManufacturingType(item);
      if (t) set.add(t);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [list]);

  const uniqueFacilityTypesFromList = useMemo(() => {
    const set = new Set();
    list.forEach((item) => {
      const t = getItemFacilityType(item);
      if (t) set.add(t);
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }));
  }, [list]);

  const sidebarIndustryOptions = useMemo(() => {
    const inList = new Set();
    list.forEach((item) => {
      getItemIndustryValues(item).forEach((v) => inList.add(v));
    });
    const base = industryFilterOptions.length
      ? industryFilterOptions
      : [...inList].map((value) => ({ value, label: value }));
    return base.filter((opt) => inList.has(opt.value));
  }, [industryFilterOptions, list]);

  const sidebarCertFilterOptions = useMemo(() => {
    const inList = new Set();
    list.forEach((item) => {
      getItemQualityCerts(item).forEach((c) => inList.add(c));
    });
    const known = CM_CERT_FILTER_OPTIONS.filter((opt) => inList.has(opt.value));
    const knownValues = new Set(known.map((o) => o.value));
    const extras = [...inList]
      .filter((value) => !knownValues.has(value))
      .sort((a, b) => a.localeCompare(b, undefined, { sensitivity: 'base' }))
      .map((value) => ({ value, label: value }));
    return [...known, ...extras];
  }, [list]);

  const displayList = useMemo(() => {
    const filtered = list.filter((item) => {
      if (verifiedOnly && (item.verification_status || '').toLowerCase() !== 'verified') return false;
      if (featuredOnly && !isFeaturedManufacturer(item)) return false;
      if (search.trim()) {
        const q = search.toLowerCase();
        const name = (item.company_name || '').toLowerCase();
        const desc = (item.company_description || '').toLowerCase();
        const locationStr = getLocation(item).toLowerCase();
        if (!name.includes(q) && !desc.includes(q) && !locationStr.includes(q)) return false;
      }
      if (ratingFilter != null && ratingFilter !== '') {
        const r = parseFloat(getRatingDisplay(item) || '0');
        if (!Number.isFinite(r) || r < Number(ratingFilter)) return false;
      }
      if (selectedIndustryFilters.length > 0) {
        const itemInd = getItemIndustryValues(item);
        const ok = selectedIndustryFilters.some((sf) => itemInd.includes(sf));
        if (!ok) return false;
      }
      if (selectedStateFilter) {
        if (getItemStateForFilter(item) !== selectedStateFilter) return false;
      }
      if (selectedCityFilter) {
        if (getItemCityForFilter(item) !== selectedCityFilter) return false;
      }
      if (selectedCmTypeFilter) {
        if (getItemContractManufacturingType(item) !== selectedCmTypeFilter) return false;
      }
      if (selectedFacilityTypeFilter) {
        if (getItemFacilityType(item) !== selectedFacilityTypeFilter) return false;
      }
      if (selectedCertFilters.length > 0) {
        const certs = getItemQualityCerts(item);
        const ok = selectedCertFilters.some((c) => certs.includes(c));
        if (!ok) return false;
      }
      return true;
    });

    const arr = [...filtered];
    if (sortBy === 'name') {
      arr.sort((a, b) => (a.company_name || '').localeCompare(b.company_name || '', undefined, { sensitivity: 'base' }));
    } else if (sortBy === 'rating') {
      arr.sort((a, b) => {
        const ra = parseFloat(getRatingDisplay(a) || '0');
        const rb = parseFloat(getRatingDisplay(b) || '0');
        if (rb !== ra) return rb - ra;
        return (a.company_name || '').localeCompare(b.company_name || '', undefined, { sensitivity: 'base' });
      });
    }
    return arr;
  }, [
    list,
    verifiedOnly,
    featuredOnly,
    search,
    ratingFilter,
    selectedIndustryFilters,
    selectedStateFilter,
    selectedCityFilter,
    selectedCmTypeFilter,
    selectedFacilityTypeFilter,
    selectedCertFilters,
    sortBy,
  ]);

  const toggleIndustryFilter = (value) => {
    setSelectedIndustryFilters((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const toggleCertFilter = (value) => {
    setSelectedCertFilters((prev) => (prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]));
  };

  const clearSidebarFilters = () => {
    setSearch('');
    setSortBy('relevance');
    setVerifiedOnly(false);
    setFeaturedOnly(false);
    setRatingFilter(null);
    setSelectedIndustryFilters([]);
    setSelectedStateFilter('');
    setSelectedCityFilter('');
    setSelectedCmTypeFilter('');
    setSelectedFacilityTypeFilter('');
    setSelectedCertFilters([]);
  };

  const stateFilterOptions = useMemo(
    () => uniqueStatesFromList.map((st) => ({ value: st, label: st })),
    [uniqueStatesFromList],
  );

  const cityFilterOptions = useMemo(
    () => uniqueCitiesFromList.map((city) => ({ value: city, label: city })),
    [uniqueCitiesFromList],
  );

  const cmTypeFilterOptions = useMemo(
    () =>
      uniqueCmTypesFromList.map((value) => ({
        value,
        label: CONTRACT_MANUFACTURING_TYPE_MAP.find((o) => o.value === value)?.label || value,
      })),
    [uniqueCmTypesFromList],
  );

  const facilityTypeFilterOptions = useMemo(
    () =>
      uniqueFacilityTypesFromList.map((value) => ({
        value,
        label: FACILITY_TYPE_MAP.find((o) => o.value === value)?.label || value,
      })),
    [uniqueFacilityTypesFromList],
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [
    industrySlug,
    search,
    sortBy,
    verifiedOnly,
    featuredOnly,
    ratingFilter,
    selectedIndustryFilters,
    selectedStateFilter,
    selectedCityFilter,
    selectedCmTypeFilter,
    selectedFacilityTypeFilter,
    selectedCertFilters,
    list.length,
  ]);

  const totalPages = displayList.length > 0 ? Math.ceil(displayList.length / pageSize) : 0;

  const paginatedList = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return displayList.slice(start, start + pageSize);
  }, [displayList, currentPage, pageSize]);

  const pageNumbers = useMemo(() => {
    if (totalPages <= 7) return Array.from({ length: totalPages }, (_, i) => i + 1);
    const pages = [1];
    if (currentPage > 3) pages.push('…');
    for (let p = Math.max(2, currentPage - 1); p <= Math.min(totalPages - 1, currentPage + 1); p += 1) {
      pages.push(p);
    }
    if (currentPage < totalPages - 2) pages.push('…');
    pages.push(totalPages);
    return pages;
  }, [totalPages, currentPage]);

  const goToPage = (page) => setCurrentPage(page);

  const selectIndustryTab = (slug) => {
    if (!slug) navigate('/contract-manufacturers');
    else navigate(`/contract-manufacturers/${slug}`);
  };

  return (
    <div className="product-marketplace-page">
      <B2BHubHomeHeader />

      <main className="product-marketplace-main">
        <nav className="product-marketplace-breadcrumb" aria-label="Breadcrumb">
          <Link to="/home">Home</Link>
          <span className="product-marketplace-breadcrumb__sep">&gt;</span>
          <Link to="/contract-manufacturers">Contract Manufacturers</Link>
          <span className="product-marketplace-breadcrumb__sep">&gt;</span>
          <span className="product-marketplace-breadcrumb__current">
            {activeIndustryLabel || industrySlug}
          </span>
        </nav>

        <div className="product-marketplace-heading">
          <div className="product-marketplace-heading__text">
            <h1>{activeIndustryLabel || 'Contract Manufacturers'}</h1>
            <p>Browse verified contract manufacturers and request quotes.</p>
          </div>
          <div className="product-marketplace-heading__actions">
            <button
              type="button"
              className="product-marketplace-back"
              onClick={() => navigate('/contract-manufacturers')}
            >
              <FaChevronLeft aria-hidden /> Back
            </button>
            <button
              type="button"
              className="product-marketplace-how"
              onClick={() =>
                toast.info('Pick an industry, apply filters, view profiles, and send RFQs to contract manufacturers.')
              }
            >
              <FaQuestionCircle aria-hidden /> How it works
            </button>
          </div>
        </div>

        {error ? (
          <div className="alert alert-warning mb-3" role="alert">
            {error}
            <button type="button" className="alert-link ms-2 btn btn-link p-0 align-baseline" onClick={doRetry}>
              Retry
            </button>
          </div>
        ) : null}
        {showingAllDueToEmpty ? (
          <div className="alert alert-info mb-3" role="alert">
            No manufacturers found for this industry. Showing all contract manufacturers.
          </div>
        ) : null}

        <div className="product-marketplace-layout">
          <aside className="product-marketplace-filters">
            <div className="product-marketplace-filters__head">
              <h2>Filters</h2>
              <button type="button" onClick={clearSidebarFilters}>
                Clear All
              </button>
            </div>

            <div className="product-marketplace-filters__field">
              <label htmlFor="cm-search">Search Manufacturer</label>
              <div className={`product-marketplace-filters__search-wrap${search ? ' has-value' : ''}`}>
                <FaSearch className="product-marketplace-filters__search-icon" aria-hidden />
                <input
                  id="cm-search"
                  type="search"
                  className="product-marketplace-filters__search-input"
                  placeholder="Search in this industry…"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                {search ? (
                  <button
                    type="button"
                    className="product-marketplace-filters__clear"
                    aria-label="Clear search"
                    onClick={() => setSearch('')}
                  >
                    <FaTimes aria-hidden />
                  </button>
                ) : null}
              </div>
            </div>

            <FilterSelect
              id="cm-state"
              label="Location (State)"
              value={selectedStateFilter}
              placeholder="All States"
              options={stateFilterOptions}
              onChange={(e) => setSelectedStateFilter(e.target.value)}
              onClear={() => setSelectedStateFilter('')}
            />

            <FilterSelect
              id="cm-city"
              label="City"
              value={selectedCityFilter}
              placeholder="All Cities"
              options={cityFilterOptions}
              onChange={(e) => setSelectedCityFilter(e.target.value)}
              onClear={() => setSelectedCityFilter('')}
            />

            <FilterSelect
              id="cm-manufacturing-type"
              label="Contract Manufacturing Type"
              value={selectedCmTypeFilter}
              placeholder="All Types"
              options={cmTypeFilterOptions}
              onChange={(e) => setSelectedCmTypeFilter(e.target.value)}
              onClear={() => setSelectedCmTypeFilter('')}
            />

            <FilterSelect
              id="cm-facility-type"
              label="Facility Type"
              value={selectedFacilityTypeFilter}
              placeholder="All Facility Types"
              options={facilityTypeFilterOptions}
              onChange={(e) => setSelectedFacilityTypeFilter(e.target.value)}
              onClear={() => setSelectedFacilityTypeFilter('')}
            />

            <details className="product-marketplace-filters__accordion" open>
              <summary>Review Rating</summary>
              <div className="product-marketplace-filters__radio-list">
                <label>
                  <input
                    type="radio"
                    name="cm-rating-filter"
                    checked={ratingFilter == null || ratingFilter === ''}
                    onChange={() => setRatingFilter(null)}
                  />
                  Any rating
                </label>
                {[5, 4, 3, 2, 1].map((n) => (
                  <label key={n}>
                    <input
                      type="radio"
                      name="cm-rating-filter"
                      checked={ratingFilter === n}
                      onChange={() => setRatingFilter(n)}
                    />
                    {n === 5 ? '5 stars & up' : `${n} stars & up`}
                  </label>
                ))}
              </div>
            </details>

            {sidebarIndustryOptions.length > 0 ? (
              <details className="product-marketplace-filters__accordion">
                <summary>{industrySlug ? 'Additional Industries' : 'Industry Type'}</summary>
                <p className="product-marketplace-filters__hint">
                  {industrySlug
                    ? 'Match manufacturers that also serve the selected industries.'
                    : 'Match any selected industry.'}
                </p>
                <div className="product-marketplace-filters__checklist">
                  {sidebarIndustryOptions.map((opt) => (
                    <label key={opt.value} className="product-marketplace-filters__check">
                      <input
                        type="checkbox"
                        checked={selectedIndustryFilters.includes(opt.value)}
                        onChange={() => toggleIndustryFilter(opt.value)}
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </details>
            ) : null}

            {sidebarCertFilterOptions.length > 0 ? (
              <details className="product-marketplace-filters__accordion">
                <summary>Quality Certifications</summary>
                <p className="product-marketplace-filters__hint">Match any selected certification.</p>
                <div className="product-marketplace-filters__checklist">
                  {sidebarCertFilterOptions.map((opt) => (
                    <label key={opt.value} className="product-marketplace-filters__check">
                      <input
                        type="checkbox"
                        checked={selectedCertFilters.includes(opt.value)}
                        onChange={() => toggleCertFilter(opt.value)}
                      />
                      <span>{opt.label}</span>
                    </label>
                  ))}
                </div>
              </details>
            ) : null}

            <label className="product-marketplace-filters__check">
              <input
                type="checkbox"
                checked={verifiedOnly}
                onChange={(e) => setVerifiedOnly(e.target.checked)}
              />
              <span>Verified manufacturers only</span>
            </label>

            <label className="product-marketplace-filters__check">
              <input
                type="checkbox"
                checked={featuredOnly}
                onChange={(e) => setFeaturedOnly(e.target.checked)}
              />
              <span>Featured manufacturers only</span>
            </label>

            <div className="product-marketplace-filters__actions">
              <button
                type="button"
                className="product-marketplace-btn product-marketplace-btn--outline"
                onClick={clearSidebarFilters}
              >
                <FaRedo aria-hidden /> Reset Filters
              </button>
            </div>
          </aside>

          <section className="product-marketplace-content">
            <div className="product-marketplace-panel product-marketplace-panel--tabs">
              <p className="product-marketplace-tabs-section__title">Industry Types</p>
              <CategoryTabsRow
                ariaLabel="Contract manufacturer industries"
                items={industryTabItems}
                activeId={industrySlug}
                onSelect={selectIndustryTab}
                allLabel="All Industries"
                fallbackIcon={FaIndustry}
              />
            </div>

            <div className="product-marketplace-panel product-marketplace-panel--products">
              <div className="product-marketplace-toolbar">
                <div className="product-marketplace-toolbar__breadcrumb">
                  {activeIndustryLabel ? (
                    <>
                      <span className="product-marketplace-toolbar__breadcrumb-label">Showing results for:</span>
                      <span className="product-marketplace-toolbar__breadcrumb-path">{activeIndustryLabel}</span>
                    </>
                  ) : (
                    <span className="product-marketplace-toolbar__breadcrumb-label">All contract manufacturers</span>
                  )}
                </div>

                <div className="product-marketplace-toolbar__controls">
                  <div className="product-marketplace-toolbar__view-toggle" aria-label="View mode">
                    <button
                      type="button"
                      className={viewMode === 'grid' ? 'is-active' : ''}
                      onClick={() => setViewMode('grid')}
                    >
                      <FaTh aria-hidden /> Grid
                    </button>
                    <button
                      type="button"
                      className={viewMode === 'list' ? 'is-active' : ''}
                      onClick={() => setViewMode('list')}
                    >
                      <FaList aria-hidden /> List
                    </button>
                  </div>
                  <select
                    className="product-marketplace-toolbar__sort"
                    value={sortBy}
                    onChange={(e) => setSortBy(e.target.value)}
                    aria-label="Sort manufacturers"
                  >
                    {CM_SORT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        Sort By: {opt.label}
                      </option>
                    ))}
                  </select>
                </div>
              </div>

              <p className="product-marketplace-count">
                {loading
                  ? 'Loading manufacturers…'
                  : `${displayList.length.toLocaleString()} Manufacturer${displayList.length !== 1 ? 's' : ''} Found`}
              </p>

              {error && !loading ? (
                <div className="product-marketplace-empty">
                  <p>{error}</p>
                  <button type="button" className="product-marketplace-btn product-marketplace-btn--primary" onClick={doRetry}>
                    Retry
                  </button>
                </div>
              ) : (
                <div className={`product-marketplace-grid product-marketplace-grid--cm product-marketplace-grid--${viewMode}`}>
                  {loading && paginatedList.length === 0
                    ? Array.from({ length: pageSize }).map((_, idx) => (
                        <div key={idx} className="product-marketplace-skeleton" />
                      ))
                    : null}
                  {!loading || paginatedList.length
                    ? paginatedList.map((item) => {
                        const imgUrl = getCardImage(item);
                        const isVerified = (item.verification_status || '').toLowerCase() === 'verified';
                        const featured = isFeaturedManufacturer(item);
                        const rating = getRatingDisplay(item);
                        const reviews = getReviewCount(item);
                        const locationStr = getLocation(item);
                        const tags = getIndustryTags(item);
                        const capacityStr = getCapacity(item);
                        const responseStr = getResponseTime(item);
                        const ordersStr = getCompletedOrders(item);
                        const rfqSent = rfqSentCompanyIds.has(Number(item.company_id));
                        const profileHref = `/contract-manufacturers/profile/${item.company_id}`;
                        const rfqHref = `/contract-manufacturers/rfq/${item.company_id}`;

                        return (
                          <article
                            key={item.company_id}
                            className={`product-marketplace-card product-marketplace-card--cm product-marketplace-card--${viewMode}`}
                          >
                            <Link to={profileHref} className="product-marketplace-card__media-wrap">
                              {featured ? (
                                <span className="product-marketplace-card__featured">Featured</span>
                              ) : null}
                              <div className="product-marketplace-card__media">
                                {imgUrl ? (
                                  <img
                                    src={resolvePublicFileUrl(imgUrl)}
                                    alt=""
                                    loading="lazy"
                                    decoding="async"
                                  />
                                ) : (
                                  <div className="product-marketplace-card__no-image">No image</div>
                                )}
                              </div>
                            </Link>
                            <div className="product-marketplace-card__body">
                              <div className="product-marketplace-card__title-row">
                                <Link to={profileHref}>
                                  <h3>{item.company_name || 'Manufacturer'}</h3>
                                </Link>
                                {isVerified ? (
                                  <span className="product-marketplace-card__verified" title="Verified">
                                    <FaCheckCircle aria-hidden />
                                  </span>
                                ) : null}
                              </div>
                              <p className="product-marketplace-card__rating">
                                <FaStar aria-hidden />
                                {rating != null ? (
                                  <>
                                    {' '}
                                    {rating}
                                    {reviews != null ? ` (${reviews} reviews)` : ''}
                                  </>
                                ) : (
                                  ' No ratings yet'
                                )}
                              </p>
                              <p className="product-marketplace-card__location">
                                <FaMapMarkerAlt aria-hidden />
                                {locationStr || 'Location not specified'}
                              </p>
                              {tags.length > 0 ? (
                                <div className="product-marketplace-card__tags">
                                  {tags.slice(0, 3).map((t) => (
                                    <span key={t} className="product-marketplace-card__tag">
                                      {t}
                                    </span>
                                  ))}
                                </div>
                              ) : null}
                              <div className="product-marketplace-card__stats">
                                <div className="product-marketplace-card__stat">
                                  <span>Capacity</span>
                                  <strong>{formatMetric(capacityStr)}</strong>
                                </div>
                                <div className="product-marketplace-card__stat">
                                  <span>Response</span>
                                  <strong>{formatMetric(responseStr)}</strong>
                                </div>
                                <div className="product-marketplace-card__stat">
                                  <span>Orders</span>
                                  <strong>{formatMetric(ordersStr)}</strong>
                                </div>
                              </div>
                              <div className="product-marketplace-card__actions">
                                <Link
                                  to={profileHref}
                                  className="product-marketplace-btn product-marketplace-btn--outline product-marketplace-card__action-btn"
                                >
                                  <FaEye aria-hidden /> View
                                </Link>
                                {rfqSent ? (
                                  <button
                                    type="button"
                                    className="product-marketplace-btn product-marketplace-card__action-btn is-sent"
                                    disabled
                                  >
                                    <FaShieldAlt aria-hidden /> RFQ Sent
                                  </button>
                                ) : (
                                  <Link
                                    to={rfqHref}
                                    className="product-marketplace-btn product-marketplace-btn--primary product-marketplace-card__action-btn"
                                  >
                                    <FaPaperPlane aria-hidden /> Request Quote
                                  </Link>
                                )}
                              </div>
                            </div>
                          </article>
                        );
                      })
                    : null}
                  {!loading && paginatedList.length === 0 ? (
                    <p className="product-marketplace-empty">No contract manufacturers match your filters yet.</p>
                  ) : null}
                </div>
              )}
            </div>

            {totalPages > 0 ? (
              <div className="product-marketplace-pagination-wrap">
                <nav className="product-marketplace-pagination" aria-label="Pagination">
                  <button
                    type="button"
                    disabled={currentPage <= 1}
                    onClick={() => goToPage(currentPage - 1)}
                  >
                    <FaChevronLeft aria-hidden /> Previous
                  </button>
                  {pageNumbers.map((page, idx) =>
                    page === '…' ? (
                      <span key={`ellipsis-${idx}`} className="product-marketplace-pagination__ellipsis">
                        …
                      </span>
                    ) : (
                      <button
                        key={page}
                        type="button"
                        className={currentPage === page ? 'is-active' : ''}
                        onClick={() => goToPage(page)}
                      >
                        {page}
                      </button>
                    ),
                  )}
                  <button
                    type="button"
                    disabled={currentPage >= totalPages}
                    onClick={() => goToPage(currentPage + 1)}
                  >
                    Next <FaChevronRight aria-hidden />
                  </button>
                </nav>
                <label className="product-marketplace-page-size">
                  Show
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    aria-label="Manufacturers per page"
                  >
                    {CM_PAGE_SIZE_OPTIONS.map((size) => (
                      <option key={size} value={size}>
                        {size}
                      </option>
                    ))}
                  </select>
                  per page
                </label>
              </div>
            ) : null}
          </section>
        </div>
      </main>
      <HubFloatingRail />
    </div>
  );
}

export default function ContractManufacturersBrowsePage() {
  const { typeSlug: industrySlugParam } = useParams();
  const [searchParams] = useSearchParams();
  const industrySlug = normalizeIndustrySlug(industrySlugParam);
  const headerQuery = readSearchQueryFromParams(searchParams);

  if (!industrySlug) {
    if (headerQuery) {
      return <ContractManufacturersListView industrySlug={undefined} />;
    }
    return <ContractManufacturersIndustryBrowse />;
  }

  return <ContractManufacturersListView industrySlug={industrySlug} />;
}
